import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import { useResource, useToast } from '../api/hooks'
import type { CreateOrganizationResult, Organization } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { PageHeader } from '../components/Layout'
import {
  Alert, ConfirmDialog, ErrorBox, Field, Spinner, Toast, describeError, relative,
} from '../components/Ui'
import { de, fmt } from '../i18n/de'

export function OrganizationsPage() {
  const navigate = useNavigate()
  const { enterOrganization } = useAuth()
  const organizations = useResource<Organization[]>('/organizations')
  const { toast, show, clear } = useToast()

  const [showCreate, setShowCreate] = useState(false)
  const [created, setCreated] = useState<CreateOrganizationResult | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Organization | null>(null)

  if (organizations.loading) return <Spinner />
  if (organizations.error) return <ErrorBox error={organizations.error} onRetry={organizations.reload} />

  const enter = async (organization: Organization) => {
    await enterOrganization(organization.id)
    navigate('/')
  }

  const remove = async (organization: Organization) => {
    try {
      await api.delete(`/organizations/${organization.id}?confirmName=${encodeURIComponent(organization.name)}`)
      setConfirmDelete(null)
      organizations.reload()
    } catch (error) {
      setConfirmDelete(null)
      show(describeError(error), 'warn')
    }
  }

  return (
    <>
      <PageHeader title={de.organizations.title}
                  actions={<button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                    {de.organizations.create}
                  </button>} />

      <div className="card">
        <table>
          <thead>
            <tr>
              <th>{de.organizations.columnName}</th>
              <th>{de.organizations.columnDoors}</th>
              <th className="num">{de.organizations.columnDoorCodes}</th>
              <th className="num">{de.organizations.columnAccounts}</th>
              <th>{de.organizations.columnActivity}</th>
              <th style={{ width: 200 }} />
            </tr>
          </thead>
          <tbody>
            {(organizations.data ?? []).map(organization => (
              <tr key={organization.id}>
                <td>
                  <strong>{organization.name}</strong>
                  {!organization.isActive && (
                    <span className="xs faint"> · {de.app.inactive}</span>
                  )}
                </td>
                <td>
                  <span className="mono">{organization.doorsOnline} / {organization.doorCount}</span>
                  {' '}
                  {organization.doorCount > 0 && organization.doorsOnline < organization.doorCount
                    ? <span className="status status-warn">⚠</span>
                    : <span className="status status-ok">●</span>}
                </td>
                <td className="num">{organization.doorCodeCount}</td>
                <td className="num">{organization.accountCount}</td>
                <td className="muted">{relative(organization.lastActivityAt)}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn btn-ghost" onClick={() => void enter(organization)}>
                    {de.organizations.enter} →
                  </button>
                  <button className="btn btn-ghost" style={{ color: 'var(--danger)' }}
                          onClick={() => setConfirmDelete(organization)}>
                    {de.app.delete}
                  </button>
                </td>
              </tr>
            ))}

            {(organizations.data ?? []).length === 0 && (
              <tr><td colSpan={6} className="faint sm">{de.app.noEntries}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 'var(--sp-4)' }}>
        <Alert kind="info">{de.organizations.initialAdminHint}</Alert>
      </div>

      {showCreate && (
        <CreateOrganizationDialog onClose={() => setShowCreate(false)}
                                  onCreated={result => {
                                    setShowCreate(false)
                                    setCreated(result)
                                    organizations.reload()
                                  }}
                                  onError={message => show(message, 'warn')} />
      )}

      {created?.initialAdmin && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgb(0 0 0 / .4)',
          display: 'grid', placeItems: 'center', zIndex: 61,
        }} role="dialog" aria-modal="true">
          <div className="card" style={{ width: 420, boxShadow: 'var(--shadow-2)' }}>
            <div className="card-h">{de.accounts.oneTimePassword}</div>
            <div className="card-b">
              <div className="sm muted" style={{ marginBottom: 'var(--sp-3)' }}>
                {created.organization.name} · {created.initialAdmin.account.email}
              </div>
              <div className="code-display" style={{ textAlign: 'center', letterSpacing: '.08em',
                                                     fontSize: 'var(--fs-xl)' }}>
                {created.initialAdmin.oneTimePassword}
              </div>
              <div className="help" style={{ textAlign: 'center', marginTop: 'var(--sp-3)' }}>
                {de.accounts.oneTimePasswordHint}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--sp-4)' }}>
                <button className="btn btn-primary" onClick={() => setCreated(null)}>
                  {de.app.close}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={fmt(de.organizations.confirmDelete, { name: confirmDelete.name })}
          detail={de.organizations.confirmDeleteDetail}
          confirmLabel={de.app.delete}
          requireText={confirmDelete.name}
          onConfirm={() => void remove(confirmDelete)}
          onCancel={() => setConfirmDelete(null)} />
      )}

      {toast && <Toast message={toast.message} kind={toast.kind} onDone={clear} />}
    </>
  )
}

function CreateOrganizationDialog({ onClose, onCreated, onError }: {
  onClose: () => void
  onCreated: (result: CreateOrganizationResult) => void
  onError: (message: string) => void
}) {
  const [name, setName] = useState('')
  const [timezone, setTimezone] = useState('Europe/Vienna')
  const [adminEmail, setAdminEmail] = useState('')
  const [adminFirst, setAdminFirst] = useState('')
  const [adminLast, setAdminLast] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    setBusy(true)
    try {
      onCreated(await api.post<CreateOrganizationResult>('/organizations', {
        name, timezone,
        initialAdmin: { email: adminEmail, firstName: adminFirst, lastName: adminLast },
      }))
    } catch (error) {
      onError(describeError(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgb(0 0 0 / .4)',
      display: 'grid', placeItems: 'center', zIndex: 60, padding: 'var(--sp-5)', overflow: 'auto',
    }} role="dialog" aria-modal="true">
      <div className="card" style={{ width: 480, boxShadow: 'var(--shadow-2)' }}>
        <div className="card-h">{de.organizations.create}</div>
        <div className="card-b">
          <Field label={de.organizations.name}>
            <input type="text" value={name} autoFocus onChange={event => setName(event.target.value)} />
          </Field>

          <Field label={de.organizations.timezone} help={de.organizations.timezoneHint}>
            <select value={timezone} onChange={event => setTimezone(event.target.value)}>
              <option value="Europe/Vienna">Europe/Vienna</option>
              <option value="Europe/Berlin">Europe/Berlin</option>
              <option value="Europe/Zurich">Europe/Zurich</option>
              <option value="UTC">UTC</option>
            </select>
          </Field>

          <hr style={{ border: 0, borderTop: '1px solid var(--border)', margin: 'var(--sp-4) 0' }} />

          <div className="sm" style={{ marginBottom: 'var(--sp-2)' }}>
            <strong>{de.organizations.initialAdmin}</strong>
          </div>
          <div className="help" style={{ marginBottom: 'var(--sp-3)' }}>
            {de.organizations.initialAdminHint}
          </div>

          <div className="grid cols-2" style={{ gap: 'var(--sp-3)' }}>
            <Field label={de.doorcodes.firstName}>
              <input type="text" value={adminFirst} onChange={event => setAdminFirst(event.target.value)} />
            </Field>
            <Field label={de.doorcodes.lastName}>
              <input type="text" value={adminLast} onChange={event => setAdminLast(event.target.value)} />
            </Field>
          </div>

          <Field label={de.accounts.columnEmail} help={de.accounts.emailHelp}>
            <input type="email" value={adminEmail} onChange={event => setAdminEmail(event.target.value)} />
          </Field>

          <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={onClose}>{de.app.cancel}</button>
            <button className="btn btn-primary"
                    disabled={busy || name.trim().length === 0 || !adminEmail.includes('@')}
                    onClick={() => void submit()}>
              {de.organizations.create}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/** Einstellungen der eigenen Organisation. */
export function OrganizationSettingsPage() {
  const organization = useResource<Organization>('/organization')
  const { toast, show, clear } = useToast()
  const { reload } = useAuth()

  if (organization.loading) return <Spinner />
  if (organization.error) return <ErrorBox error={organization.error} onRetry={organization.reload} />
  if (!organization.data) return <ErrorBox error={new Error('not found')} />

  const detail = organization.data

  const patch = async (body: Record<string, unknown>) => {
    try {
      await api.patch('/organization', body)
      organization.reload()
      await reload()
      show(de.app.save)
    } catch (error) {
      show(describeError(error), 'warn')
    }
  }

  return (
    <>
      <PageHeader title={de.nav.organization} subtitle={detail.name} />

      <div className="card" style={{ maxWidth: 560 }}>
        <div className="card-b">
          <Field label={de.organizations.name}>
            <input type="text" defaultValue={detail.name}
                   onBlur={event => {
                     if (event.target.value !== detail.name) void patch({ name: event.target.value })
                   }} />
          </Field>

          <Field label={de.organizations.timezone} help={de.organizations.timezoneHint}>
            <select defaultValue={detail.timezone}
                    onChange={event => void patch({ timezone: event.target.value })}>
              <option value="Europe/Vienna">Europe/Vienna</option>
              <option value="Europe/Berlin">Europe/Berlin</option>
              <option value="Europe/Zurich">Europe/Zurich</option>
              <option value="UTC">UTC</option>
            </select>
          </Field>

          {/* Es gibt bewusst keine Felder für Aufbewahrung (F6) und Offline-Verhalten (F11). */}
          <Alert kind="info">{de.organizations.noSettingsHint}</Alert>
        </div>
      </div>

      {toast && <Toast message={toast.message} kind={toast.kind} onDone={clear} />}
    </>
  )
}
